import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Combobox,
  ErrorState,
  FormStack,
  Page,
  Switch,
  Text,
} from "@marekl/adminer/components";
import { useConfirm, useT } from "@marekl/adminer/hooks";
import { apiErrorMessage, notifySuccess } from "@marekl/adminer/api";
import { pluralizeCzech } from "@marekl/adminer/format";
import { UnsavedChangesModal, useUnsavedChangesGuard } from "@marekl/adminer/shell";
import { AppTopbar } from "../../../shell/AppTopbar";
import { ROLE_LABEL } from "../../../lib/i18n/roles";
import {
  readsOnlyAsGlobalViewer,
  useMe,
  useUsers,
  type SystemRole,
  type User,
} from "../../../auth/queries";
import type { ID } from "../../../lib/id";
import {
  useAccessSettings,
  usePreviewAccess,
  useSaveAccess,
  type AccessImpact,
  type AccessPayload,
  type AccessSettings,
} from "../../../lib/queries/access";
import type { RegistrationFlow } from "../../../lib/queries/registration";
import { FLOW_CHOICES } from "../../public/TeamRegistrationPage/flows";
import { useSeason } from "../../../season";
import { seasonWindowSentence } from "./seasonWindow";
import styles from "./AccessPage.module.css";

// Admin always logs in, so it is not offered: a box that could be
// unticked would promise a lockout the server refuses.
const GATE_ROLES = (Object.keys(ROLE_LABEL) as SystemRole[]).filter((r) => r !== "admin");

function toPayload(s: AccessSettings): AccessPayload {
  return {
    login_restricted: s.login_restricted,
    login_roles: s.login_roles,
    exceptions: s.exceptions.map((e) => e.user_id),
    closed_flows: s.closed_flows,
  };
}

function samePayload(a: AccessPayload, b: AccessPayload): boolean {
  const same = (x: string[], y: string[]) =>
    x.length === y.length && [...x].sort().join() === [...y].sort().join();
  return (
    a.login_restricted === b.login_restricted &&
    same(a.login_roles, b.login_roles) &&
    same(a.exceptions, b.exceptions) &&
    same(a.closed_flows, b.closed_flows)
  );
}

function userLabel(u: User): string {
  return u.display_name ? `${u.display_name} (${u.email})` : u.email;
}

function impactSentence(imp: AccessImpact): string {
  const people = pluralizeCzech(imp.users, ["přihlášeného uživatele", "přihlášené uživatele", "přihlášených uživatelů"]);
  return `Uložením odhlásíte ${imp.users} ${people}. Při dalším přihlášení je systém odmítne.`;
}

export function AccessPage() {
  const { data: me } = useMe();
  // Čtení všeho sees who may log in and which doors are open, and changes
  // neither: every control is disabled and there is nothing to save.
  const readOnly = readsOnlyAsGlobalViewer(me);
  const { data, isLoading, error, refetch } = useAccessSettings();
  const { data: users = [] } = useUsers();
  const { activeSeason } = useSeason();
  const preview = usePreviewAccess();
  const save = useSaveAccess();
  const confirm = useConfirm();
  const t = useT();
  const [draft, setDraft] = useState<AccessPayload | null>(null);

  // The stored policy seeds the draft, and a refetch reseeds it only while
  // the draft is clean (still what was last seeded): a focus refetch must
  // not throw away edits the admin has not saved yet. After a save the
  // draft already equals what comes back, so nothing is lost either way.
  const stored = useMemo(() => (data ? toPayload(data) : null), [data]);
  const seeded = useRef<AccessPayload | null>(null);
  useEffect(() => {
    if (!stored) return;
    const previous = seeded.current;
    setDraft((d) => (d === null || (previous && samePayload(d, previous)) ? stored : d));
    seeded.current = stored;
  }, [stored]);

  const dirty = !!draft && !!stored && !samePayload(draft, stored);
  const guard = useUnsavedChangesGuard(dirty);

  async function commit(payload: AccessPayload) {
    const imp = await save.mutateAsync(payload);
    notifySuccess(
      imp.users > 0
        ? `Uloženo. Odhlášení uživatelé: ${imp.users}.`
        : "Nastavení přístupu je uloženo.",
    );
  }

  async function onSave() {
    if (!draft) return;
    let imp: AccessImpact;
    try {
      imp = await preview.mutateAsync(draft);
    } catch {
      return; // rendered beside the button
    }
    if (imp.users === 0) {
      try {
        await commit(draft);
      } catch {
        // rendered beside the button
      }
      return;
    }
    confirm({
      title: "Uložit a odhlásit",
      variant: "danger",
      description: impactSentence(imp),
      confirmLabel: "Uložit a odhlásit",
      onConfirm: () => commit(draft),
    });
  }

  if (error) {
    return (
      <>
        <AppTopbar crumbs={[{ label: "Přístup" }]} />
        <Page>
          <Page.Content>
            <ErrorState error={error} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  const usersById = new Map(users.map((u) => [u.id, u]));
  // An exception whose user is not in the list yet (still loading) keeps
  // its stored name rather than vanishing from the picker.
  const exceptionOptions: User[] = [
    ...users,
    ...(data?.exceptions ?? [])
      .filter((e) => !usersById.has(e.user_id))
      .map((e) => ({ id: e.user_id, email: e.email, display_name: e.display_name }) as User),
  ];
  const setFlowOpen = (flow: RegistrationFlow, open: boolean) =>
    draft &&
    setDraft({
      ...draft,
      closed_flows: open
        ? draft.closed_flows.filter((f) => f !== flow)
        : [...draft.closed_flows, flow],
    });
  const failure = preview.error ?? save.error;

  return (
    <>
      <AppTopbar crumbs={[{ label: "Přístup" }]} />
      <Page>
        <Page.Header
          title="Přístup"
          subtitle="kdo se smí přihlásit a které registrace týmů jsou otevřené"
        />
        <Page.Content stack gap="lg">
          <Card>
            <Card.Header title="Přihlášení" />
            <Card.Content isLoading={isLoading || !draft}>
              {draft && (
                <FormStack>
                  <Switch
                    label="Omezit přihlášení na vybrané role"
                    checked={draft.login_restricted}
                    disabled={readOnly}
                    onChange={(e) => setDraft({ ...draft, login_restricted: e.target.checked })}
                    hint="Ostatní účty se nepřihlásí; obnova hesla jim funguje dál. Administrátor se přihlásí vždy."
                  />
                  {draft.login_restricted && (
                    <>
                      <Combobox<SystemRole, SystemRole>
                        multiple
                        label="Smí se přihlásit"
                        placeholder="Jen administrátoři"
                        options={GATE_ROLES}
                        getOptionLabel={(r) => ROLE_LABEL[r]}
                        getOptionValue={(r) => r}
                        getOptionKey={(r) => r}
                        value={draft.login_roles}
                        disabled={readOnly}
                        onChange={(login_roles) => setDraft({ ...draft, login_roles })}
                      />
                      <Combobox<User, ID>
                        multiple
                        label="Výjimky"
                        placeholder="Žádné"
                        options={exceptionOptions}
                        getOptionLabel={userLabel}
                        getOptionValue={(u) => u.id}
                        getOptionKey={(u) => u.id}
                        value={draft.exceptions}
                        disabled={readOnly}
                        onChange={(exceptions) => setDraft({ ...draft, exceptions })}
                        hint="Účty, které se přihlásí bez ohledu na své role, třeba vedoucí v pilotním provozu."
                      />
                    </>
                  )}
                </FormStack>
              )}
            </Card.Content>
          </Card>

          <Card>
            <Card.Header title="Registrace týmů" />
            <Card.Content isLoading={isLoading || !draft}>
              {draft && (
                <FormStack>
                  {FLOW_CHOICES.map((c) => (
                    <Switch
                      key={c.value}
                      label={c.unlisted ? `${c.title} (jen přes odkaz)` : c.title}
                      checked={!draft.closed_flows.includes(c.value)}
                      disabled={readOnly}
                      onChange={(e) => setFlowOpen(c.value, e.target.checked)}
                      hint={c.description}
                    />
                  ))}
                  <Text variant="muted">
                    Vypnutá registrace se na stránce registrace ukáže jako uzavřená. Když vypnete
                    všechny, registrace nových týmů je uzavřená úplně.
                  </Text>
                  {/* The other control over „Člen ČNSMS", shown so the whole
                      answer to „can a club join now" is on one page. */}
                  <Alert variant="info" title="Člen ČNSMS a registrační okno">
                    „Člen ČNSMS“ se na stránce registrace nabízí jen v registračním okně sezóny,
                    i když je přepínač výše zapnutý. {seasonWindowSentence(activeSeason)}{" "}
                    <Link to="/admin/sezony">{readOnly ? "Zobrazit v Sezónách" : "Upravit v Sezónách"}</Link>
                  </Alert>
                </FormStack>
              )}
            </Card.Content>
          </Card>

          {failure && <Alert variant="err">{apiErrorMessage(failure, t)}</Alert>}

          {!readOnly && (
          <div className={styles.actions}>
            <Button
              variant="primary"
              disabled={!dirty}
              loading={preview.isPending || save.isPending}
              onClick={onSave}
            >
              Uložit
            </Button>
          </div>
          )}
        </Page.Content>
      </Page>

      <UnsavedChangesModal
        open={guard.blocked}
        description="Nastavení přístupu zatím není uložené. Opustit stránku a zahodit změny?"
        onClose={guard.cancel}
        onConfirm={guard.proceed}
      />
    </>
  );
}
